import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Dropdown, Menu, Skeleton, Table } from 'antd';
import { Spin } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CopySvg, DeleteSvg } from '../../../../assets/icons';
import AppreciateSvg from '../../../../assets/icons/AppreciateSvg';
import Editsvg from '../../../../assets/icons/EditSvg';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import MoreSvg from '../../../../assets/icons/MoreSvg';
import theme from '../../../../assets/theme';
import { useDesignerDataContext } from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useRunTestcases from '../../../../hooks/useRunTestcases';
import useNewTestcase from '../../../../hooks/useTestcase';
import useTestcase from '../../../../hooks/useTestcase';
import useTestcases from '../../../../hooks/useTestcases';
import { LessonAttachment } from '../../../../services/backendService/backendService';
import {
  ById,
  PromptInput,
  PromptInputData,
  PromptSingleTestCaseResult,
  PromptTestcase,
} from '../../../../services/backendService/types';
import { AiContent } from '../../../../services/TemplateService/Template.type';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import LiveEmptyState from '../../../_molecules/LiveEmptyState';
import { Slide } from '../../../SlideComponents/slide.types';
import {
  GeneratorForm,
  GeneratorFormState,
  useGeneratorFormState,
} from '../PromptsTab/GeneratorForm';

const TestCasesTab = () => {
  const aiContent = useDesignerDataContext(c => c.state.localTemplate?.aiContent);
  const presentationId = useDesignerDataContext(c => c.state.localTemplate?.id);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const { testcases, isFetchingTestcases } = useTestcases({
    presentationId,
  });
  const { isRunningTestcases, runTestcases } = useRunTestcases();
  const [error, setError] = useState<
    | {
        message: string;
        status?: number;
        rawResponse: string;
      }
    | undefined
  >(undefined);

  const handleRunTestcases = useCallback(
    (testcaseIds?: string[], useSlidesAsSampleLesson?: boolean) => {
      if (!presentationId) return;
      if (!testcases) return;
      runTestcases({
        presentationId,
        useSlidesAsSampleLesson,

        onSuccessFunction: (
          results: ById<{
            slides: Slide[];
            allTestcasePromptOutputs?: PromptSingleTestCaseResult;
            lessonAttachments?: LessonAttachment[];
            error?: string;
          }>,
        ) => {
          const testResultPayLoad: {
            id: string;
            inputs: PromptInputData[];
            slides: Slide[];
            allTestcasePromptOutputs?: PromptSingleTestCaseResult;
            lessonAttachments?: LessonAttachment[];
            isSuggestion: boolean;
          }[] = Object.entries(results).reduce((acc, [key, value]) => {
            // merges the testresults and the testcases into a single payload
            const testcase = testcases.find(r => r.id === key);
            if (!testcase) return acc;
            const newTestcaseResult = {
              id: testcase.id,
              inputs: testcase.inputs,
              slides: value.slides,
              lessonAttachments: value.lessonAttachments,
              allTestcasePromptOutputs: value.allTestcasePromptOutputs,
              isSuggestion: testcase.isSuggestion,
              error: value.error,
            };
            return [...acc, newTestcaseResult];
          }, [] as { id: string; inputs: PromptInputData[]; slides: Slide[]; isSuggestion: boolean; lessonAttachments?: LessonAttachment[] }[]);
          if (testResultPayLoad.length === 0) return;
          dispatch({
            type: DesignerUIStateActions.setTestcaseResults,
            payload: testResultPayLoad,
          });
          dispatch({
            type: DesignerUIStateActions.setSelectedTestcaseResult,
            payload: testResultPayLoad[0],
          });
          dispatch({
            type: DesignerUIStateActions.setSelectedTab,
            payload: {
              tab: 'testCasesResult',
            },
          });
        },
        onErrorFunction: error => {
          setError(error);
        },

        testcaseIds: testcaseIds ? testcaseIds : testcases.map(r => r.id),
      });
    },
    [dispatch, presentationId, runTestcases, testcases],
  );

  if (!presentationId) return null;
  if (!aiContent)
    return (
      <>
        {testcases && testcases.length === 0 && (
          <>
            <LiveEmptyState
              title={'To test your customizable lesson, first try adding some prompts'}
              style={{
                margin: '20px',
                width: '90%',
                backgroundColor: 'white',
                border: 'none',
              }}
              icon={<AppreciateSvg />}
            />
          </>
        )}
      </>
    );
  return (
    <FullWidthContainer>
      <BaseModal visible={!!error} onCancel={() => setError(undefined)}>
        <h1>Something went wrong</h1>
        <p>
          <strong>Status: </strong>
          {error?.status}
        </p>
        <p>
          <strong>Message: </strong>
          {error?.message}
        </p>
        <p>{error?.rawResponse}</p>
      </BaseModal>
      <ActionTopBar
        aiContent={aiContent}
        presentationId={presentationId}
        handleRunTestcases={handleRunTestcases}
        nTestcases={testcases?.length}
        isRunningTestcases={isRunningTestcases}
        testcases={testcases}
      />
      {testcases && (
        <TestCasesTable
          testcases={testcases}
          promptInputs={aiContent.promptInputs}
          presentationId={presentationId}
          handleRunTestcases={handleRunTestcases}
          isRunningTestcases={isRunningTestcases}
        />
      )}
      {!testcases && isFetchingTestcases && <LoadingTable />}
      {testcases && testcases.length === 0 && !isFetchingTestcases && (
        <>
          {testcases && testcases.length === 0 && (
            <>
              <LiveEmptyState
                title={
                  'No testcases yet, add a testcase to test your customizable lesson'
                }
                style={{
                  margin: '20px',
                  width: '90%',
                  backgroundColor: 'white',
                  border: 'none',
                }}
                icon={<AppreciateSvg />}
              />
            </>
          )}
        </>
      )}
    </FullWidthContainer>
  );
};

export default TestCasesTab;

const ActionTopBar = ({
  aiContent,
  presentationId,
  nTestcases = 0,
  handleRunTestcases,
  isRunningTestcases,
  testcases,
}: {
  aiContent: AiContent;
  presentationId: string;
  nTestcases?: number;
  handleRunTestcases: (testcaseIds?: string[], useSlidesAsSampleLesson?: boolean) => void;
  isRunningTestcases: boolean;
  testcases?: PromptTestcase[];
}) => {
  const [showAddTestCaseModal, setShowAddTestCaseModal] = useState(false);
  const testcaseResults = useDesignerUIContext(c => c.state.testcaseResults);
  const dispatch = useDesignerUIContext(c => c.dispatch);
  const { newTestcase } = useNewTestcase({ presentationId });
  const handleAddCase = (formState: GeneratorFormState) => {
    newTestcase({
      inputData: generatorFormStateToUserInputFormData(formState, aiContent.promptInputs),
    });
    setShowAddTestCaseModal(false);
  };

  const handleSyncSampleLessons = () => {
    if (!testcases) return;
    const sampleLessonTestCases = testcases.filter(testcase => testcase.isSampleLesson);
    handleRunTestcases(
      sampleLessonTestCases.map(testcase => testcase.id),
      true,
    );
  };

  return (
    <ActionTopBarContainer>
      {testcaseResults && (
        <Button
          type="secondary"
          onClick={() => {
            dispatch({
              type: DesignerUIStateActions.setSelectedTab,
              payload: {
                tab: 'testCasesResult',
              },
            });
          }}
        >
          See results
        </Button>
      )}
      <BaseModal
        title="Basic Modal"
        visible={showAddTestCaseModal}
        onCancel={() => setShowAddTestCaseModal(false)}
      >
        <NewTestCase
          isSavingTestCase={false}
          promptInputs={aiContent.promptInputs}
          onSave={handleAddCase}
        />
      </BaseModal>
      <ActionTopBarRightContainer>
        <Button type="ghost" onClick={() => setShowAddTestCaseModal(true)}>
          Add testcase
        </Button>

        <Button
          type="ghost"
          disabled={nTestcases === 0}
          onClick={handleSyncSampleLessons}
          loading={isRunningTestcases}
        >
          Sync sample lessons
        </Button>

        <Button
          type="secondary"
          disabled={nTestcases === 0}
          onClick={() => handleRunTestcases(undefined)}
          loading={isRunningTestcases}
          icon={
            <IconWrapper>
              <MagicWandSvg />
            </IconWrapper>
          }
        >
          Run all {nTestcases} testcases
        </Button>
      </ActionTopBarRightContainer>
    </ActionTopBarContainer>
  );
};

const NewTestCase = ({
  onSave,
  promptInputs,
  testCase,
}: {
  onSave: (formState: GeneratorFormState) => void;
  isSavingTestCase: boolean;
  promptInputs: PromptInput[];
  testCase?: PromptTestcase;
}) => {
  const existingFormState: GeneratorFormState | undefined = useMemo(() => {
    if (!testCase) return undefined;
    return testCase.inputs.reduce(
      (acc, input) => ({ ...acc, [input.id]: input.value }),
      {},
    );
  }, [testCase]);

  const { formState, handleInputChange } = useGeneratorFormState(
    promptInputs,
    existingFormState,
  );

  const handleSubmit = () => {
    onSave(formState);
  };

  return (
    <>
      <PromptInputForm>
        <GeneratorForm
          promptInputs={promptInputs}
          handleSubmit={handleSubmit}
          handleInputChange={handleInputChange}
          formState={formState}
        />
      </PromptInputForm>
    </>
  );
};

const TestCasesTable = ({
  testcases,
  promptInputs,
  presentationId,
  handleRunTestcases,
  isRunningTestcases,
}: {
  testcases: PromptTestcase[];
  promptInputs: PromptInput[];
  presentationId: string;
  handleRunTestcases: (testcaseIds: string[]) => void;
  isRunningTestcases: boolean;
}) => {
  const [selectedTestcases, setSelectedTestcases] = useState<React.Key[]>([]);
  const [editingTestcase, setEditingTestcase] = useState<PromptTestcase | undefined>(
    undefined,
  );
  const { editTestcase, isLoading, deleteTestcase, duplicateTestcase } = useTestcase({
    presentationId,
  });
  const handleDuplicateTestcase = useCallback(
    (testcaseid: string) => {
      duplicateTestcase({
        testcaseid,
      });
    },
    [duplicateTestcase],
  );
  const handleDeleteTestcase = useCallback(
    ({ ids }: { ids?: string[] }) => {
      if (!ids) return;
      onSelectedTestcaseChange([]);
      deleteTestcase({ ids });
    },
    [deleteTestcase],
  );

  const handleUseAsSugestion = (
    shouldUseAsSuggestion: boolean,
    testcaseId: string,
    inputs: PromptInputData[],
    isSampleLesson: boolean,
  ) => {
    editTestcase({
      inputData: inputs.map(input => {
        return {
          id: input.id,
          type: input.type,
          value: input.value as string | undefined,
        };
      }),
      isSuggestion: shouldUseAsSuggestion,
      id: testcaseId,
      isSampleLesson,
    });
  };

  const handleUseAsSampleLesson = (
    shouldUseAsSampleLesson: boolean,
    testcaseId: string,
    inputs: PromptInputData[],
    isSuggestions: boolean,
  ) => {
    editTestcase({
      inputData: inputs.map(input => {
        return {
          id: input.id,
          type: input.type,
          value: input.value as string | undefined,
        };
      }),
      isSampleLesson: shouldUseAsSampleLesson,
      id: testcaseId,
      isSuggestion: isSuggestions,
    });
  };

  const onSelectedTestcaseChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedTestcases(newSelectedRowKeys);
  };

  const rowSelection = useMemo(() => {
    return {
      selectedRowKeys: selectedTestcases,
      onChange: onSelectedTestcaseChange,
    };
  }, [selectedTestcases]);

  const tableColumns = useMemo(() => {
    const columns = promptInputs.map(input => {
      return {
        title: input.label,
        dataIndex: input.id,
        align: 'left',
        key: input.id,
        render: (text: string, record: { testcase: PromptTestcase }) => {
          return (
            <span
              onClick={() => {
                setEditingTestcase(record.testcase);
              }}
            >
              {text && text.length >= 50 ? text.slice(0, 50) + '...' : text}
            </span>
          );
        },
      };
    });

    columns.push({
      title: 'Use as suggestion?',
      dataIndex: 'isSuggestion',
      align: 'center',
      key: 'isSuggestion',
      render: (text: string, record: { testcase: PromptTestcase }) => {
        return (
          <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              checked={record.testcase.isSuggestion}
              onChange={shouldUseAsSuggestion => {
                handleUseAsSugestion(
                  shouldUseAsSuggestion.target.checked,
                  record.testcase.id,
                  record.testcase.inputs,
                  record.testcase.isSampleLesson,
                );
              }}
            />
          </span>
        );
      },
    });

    columns.push({
      title: 'Use as Sample lesson',
      dataIndex: 'isSampleLesson',
      align: 'center',
      key: 'isSampleLesson',
      render: (text: string, record: { testcase: PromptTestcase }) => {
        return (
          <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              checked={record.testcase.isSampleLesson}
              onChange={shouldUseAsSampleLesson => {
                handleUseAsSampleLesson(
                  shouldUseAsSampleLesson.target.checked,
                  record.testcase.id,
                  record.testcase.inputs,
                  record.testcase.isSuggestion,
                );
              }}
            />
          </span>
        );
      },
    });

    return [
      {
        title: '',
        dataIndex: 'index',
        key: 'index',
        align: 'left',
        width: '20px',
      },
      ...(columns ?? []),
      {
        title: 'options',
        dataIndex: 'options',
        key: 'options',
        align: 'center',
        width: '20px',
        // @ts-ignore
        render: (_, record: { testcase: PromptTestcase }) => {
          return (
            <div
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      onClick={() => {
                        handleRunTestcases([record.testcase.id]);
                      }}
                      className="align-center"
                      icon={
                        <IconWrapper>
                          {isRunningTestcases ? (
                            <Spin
                              indicator={
                                <LoadingOutlined style={{ fontSize: 16 }} spin />
                              }
                            />
                          ) : (
                            <MagicWandSvg />
                          )}
                        </IconWrapper>
                      }
                    >
                      Run this testcase
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        setEditingTestcase(record.testcase);
                      }}
                      className="align-center"
                      icon={
                        <IconWrapper>
                          <Editsvg />
                        </IconWrapper>
                      }
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        handleDuplicateTestcase(record.testcase.id);
                      }}
                      icon={
                        <IconWrapper>
                          <CopySvg />
                        </IconWrapper>
                      }
                    >
                      Duplicate
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => handleDeleteTestcase({ ids: [record.testcase.id] })}
                      icon={
                        <IconWrapper>
                          <DeleteSvg />
                        </IconWrapper>
                      }
                    >
                      Delete
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                placement="bottomRight"
              >
                <IconWrapper color="#A5A5A5" height="24px" width="24px">
                  <MoreSvg />
                </IconWrapper>
              </Dropdown>
            </div>
          );
        },
      },
    ];
  }, [handleDeleteTestcase, handleDuplicateTestcase, promptInputs]);

  const tableData = useMemo(() => {
    if (testcases.length === 0) return undefined;
    const data = testcases.map((testcase, i) => {
      const row: any = {};
      testcase.inputs.forEach(input => {
        row[input.id] = input.value;
      });
      row['testcase'] = testcase;
      row['index'] = i + 1;
      row['key'] = testcase.id;
      return row;
    });
    return data;
  }, [testcases]);

  const handleSaveTestCase = (formState: GeneratorFormState) => {
    if (!editingTestcase) return;
    editTestcase({
      inputData: promptInputs.map(input => {
        return {
          id: input.id,
          type: input.type,
          value: formState[input.id] as string | undefined,
        };
      }),
      isSuggestion: editingTestcase.isSuggestion,
      isSampleLesson: editingTestcase.isSampleLesson,
      id: editingTestcase.id,
      onSuccess: () => setEditingTestcase(undefined),
    });
  };

  return (
    <TestCasesTableContainer>
      <BaseModal
        title="Edit test case"
        visible={editingTestcase !== undefined}
        onCancel={() => setEditingTestcase(undefined)}
      >
        <NewTestCase
          isSavingTestCase={isLoading}
          promptInputs={promptInputs}
          testCase={editingTestcase}
          onSave={handleSaveTestCase}
        />
      </BaseModal>

      <Table
        rowSelection={rowSelection}
        dataSource={tableData}
        // @ts-ignore
        columns={tableColumns}
        pagination={false}
      />
      {selectedTestcases.length > 0 && (
        <MultiSelectTools>
          <p>
            {selectedTestcases.length} testcase{selectedTestcases.length > 1 ? 's' : ''}{' '}
            selected
          </p>
          <div style={{ display: 'flex', gap: 20 }}>
            <Button
              icon={<DeleteSvg />}
              onClick={() => handleDeleteTestcase({ ids: selectedTestcases as string[] })}
            >
              Delete {selectedTestcases.length} testcase
              {selectedTestcases.length > 1 ? 's' : ''}
            </Button>
            <Button
              icon={<MagicWandSvg />}
              loading={isRunningTestcases}
              onClick={() => handleRunTestcases(selectedTestcases as string[])}
            >
              Run {selectedTestcases.length} testcase
              {selectedTestcases.length > 1 ? 's' : ''}
            </Button>
          </div>
        </MultiSelectTools>
      )}
    </TestCasesTableContainer>
  );
};

type TableSkeletonDataType = {
  input: string;
  name: string;
  moreInputs: string;
  options: string;
};

const LoadingTable = ({
  hideHeader,
  rows,
}: {
  hideHeader?: boolean | null;
  rows?: number | null;
}) => {
  const { t } = useTranslation('Host');

  const tableData: TableSkeletonDataType[] = new Array(rows ? rows : 5)
    .fill(0)
    .map((i, index) => ({
      input: '',
      moreInputs: '',
      name: '',
      options: '',
    }));

  const columns: ColumnType<TableSkeletonDataType>[] = [
    {
      title: t('Name'),
      dataIndex: ['name'],
      key: 'name',
      render: () => (
        <>
          <Skeleton.Input active={true} style={{ width: '100%' }} />
        </>
      ),
    },

    {
      title: t('Options'),
      dataIndex: 'actions',
      align: 'center',
      key: 'options',
      render: () => (
        <>
          <Skeleton.Input active={true} />
        </>
      ),
    },
  ];

  return (
    <TableContainer>
      <Table
        rowKey="id"
        dataSource={tableData}
        columns={columns}
        pagination={false}
        showHeader={hideHeader ? false : true}
      />
    </TableContainer>
  );
};

const MultiSelectTools = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: ${theme.colors.green};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;

  p {
    color: white;
    margin: 0;
  }

  button {
    border: 1px solid ${theme.colors.white};
  }
`;

const ActionTopBarContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
  height: 50px;
`;
const ActionTopBarRightContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
  height: 50px;
`;
const FullWidthContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background-size: cover;
  padding: 20px;
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
  #align-center {
    .ant-table-cell {
      text-align: center !important;
    }
  }
`;

const TableContainer = styled.div`
  .ant-skeleton-element {
    width: 100%;
  }
  .ant-skeleton-input {
    border-radius: 5px;
  }
  .ant-table-cell {
    padding: 15px;
    height: 60px;
  }
`;
const PromptInputForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 0;
`;
const TestCasesTableContainer = styled.div`
  .ant-table-row {
    .ant-table-selection-column {
      min-width: 40px;
      position: relative;
      height: 100%;
      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0px;
        bottom: 0;
        height: 60%;
        width: 1px;
        margin: auto;
        background-color: ${theme.colors.greyLight};
      }
    }
  }
`;
